import { useState } from 'react';
import { Popover, TextField, IconButton, Typography, Tooltip } from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import { SketchPicker } from 'react-color';

interface CustomColorBuilderProps {
  onAdd: (name: string, color: string) => void;
  existingColors: Record<string, string>;
  onRemove: (name: string) => void;
  colorPalette: string[];
  onRemoveAll: () => void;
  customList: string[];
  colorMap: Record<string, string>;
  onColorChange?: (name: string, color: string) => void;
  iconColor?: string;
}

const CustomColorBuilder = ({
  onAdd,
  onRemove,
  onRemoveAll,
  customList,
  colorMap,
  colorPalette,
  onColorChange,
  iconColor,
}: CustomColorBuilderProps) => {
  const [newName, setNewName] = useState('');
  const [anchorEl, setAnchorEl] = useState<HTMLDivElement | null>(null);
  const [editingItem, setEditingItem] = useState<string | null>(null);

  const getRandomColor = () => {
    if (!colorPalette || colorPalette.length === 0) return '#333333';
    const randomIndex = Math.floor(Math.random() * colorPalette.length);
    return colorPalette[randomIndex];
  };

  const [newColor, setNewColor] = useState(getRandomColor());

  const handleAdd = () => {
    if (newName.trim()) {
      onAdd(newName.trim(), newColor);
      setNewName('');
      setNewColor(getRandomColor());
    }
  };

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter') {
      handleAdd();
    }
  };

  const openColorPicker = (event: React.MouseEvent<HTMLDivElement>, itemName?: string) => {
    setAnchorEl(event.currentTarget);
    setEditingItem(itemName || null);
  };

  const closeColorPicker = () => {
    setAnchorEl(null);
    setEditingItem(null);
  };

  const handleColorChange = (color: string) => {
    if (editingItem && onColorChange) {
      onColorChange(editingItem, color);
    } else {
      setNewColor(color);
    }
  };

  const getCurrentColor = () => {
    if (editingItem) {
      return colorMap[editingItem] || '#ccc';
    }
    return newColor;
  };

  const open = Boolean(anchorEl);

  const deleteButtonSx = {
    color: 'lightgray',
    '&:hover': {
      color: 'gray',
      backgroundColor: 'transparent',
    },
  };

  return (
    <div
      style={{
        marginTop: '10px',
        paddingLeft: '10px',
        borderTop: '1px dashed #ccc',
        paddingTop: '10px',
      }}
    >
      {/* Header Section */}
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: customList.length > 0 ? '10px' : '0px',
          height: '30px',
        }}
      >
        <Typography variant="caption" style={{ color: iconColor || '#888', fontWeight: 600 }}>
          Additional Values
        </Typography>

        {customList.length > 0 && (
          <Tooltip title="Remove all values">
            <IconButton size="small" onClick={onRemoveAll} sx={deleteButtonSx}>
              <DeleteIcon style={{ fontSize: 16 }} />
            </IconButton>
          </Tooltip>
        )}
      </div>

      {/* List Section */}
      <div style={{ textAlign: 'left', marginBottom: '10px' }}>
        {customList.map((val) => (
          <div
            key={val}
            style={{
              display: 'flex',
              alignItems: 'center',
              marginTop: '8px',
              width: '100%',
            }}
          >
            <div
              onClick={(e) => openColorPicker(e, val)}
              style={{
                height: '1rem',
                width: '30px',
                backgroundColor: colorMap[val] || '#ccc',
                border: '1px solid darkgray',
                marginRight: '15px',
                borderRadius: '2px',
                minWidth: '30px',
                cursor: 'pointer',
              }}
              title="Click to change color"
            />
            <label
              style={{
                fontSize: '12px',
                color: iconColor ? iconColor : '#333',
                flexGrow: 1,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                cursor: 'default',
              }}
              title={val}
            >
              {val}
            </label>

            <IconButton
              size="small"
              onClick={() => onRemove(val)}
              title="Remove value"
              sx={deleteButtonSx}
            >
              <DeleteIcon style={{ fontSize: 16 }} />
            </IconButton>
          </div>
        ))}
      </div>

      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          // background: '#f9f9f9',
          borderRadius: '4px',
          marginTop: '10px',
        }}
      >
        <div
          onClick={(e) => openColorPicker(e)}
          style={{
            height: '1rem',
            width: '30px',
            backgroundColor: newColor,
            border: '1px solid darkgray',
            marginRight: '7px',
            borderRadius: '2px',
            minWidth: '30px',
            cursor: 'pointer',
          }}
          title="Pick Color"
        />
        <TextField
          size="small"
          placeholder="Type name & Press Enter..."
          value={newName}
          onChange={(e) => setNewName(e.target.value)}
          onKeyDown={handleKeyDown}
          sx={{
            flexGrow: 1,
            paddingRight: '8px',
            '& .MuiInputBase-input': {
              fontSize: '12px',
              padding: '4px 8px',
              height: '20px',
            },
            '& .MuiOutlinedInput-root': { backgroundColor: 'white' },
          }}
        />
      </div>

      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={closeColorPicker}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      >
        <SketchPicker
          color={getCurrentColor()}
          disableAlpha
          onChangeComplete={(c) => handleColorChange(c.hex)}
        />
      </Popover>
    </div>
  );
};

export default CustomColorBuilder;
